/**
 * Documentation: https://docs.google.com/document/d/1MHL7Ddir0LVCjjcWfGE7MjciHXN-SXMFBQg2KRMiZRk/edit?usp=sharing
 */

import React, {
    isValidElement,
    PropsWithChildren,
    useEffect,
    useMemo,
} from "react";
import clsx from "clsx";

import { flattenChildren } from "~utils/flattenChildren";

import styles from "./stickyScroll.module.css";

/**
 * StickyScroll exists to separate out the sticky logic from other logic. It provides the following benefits:
 * 1) Dynamically slices the `StickyScroll.Segment` children so only a certain # of children is shown at any given moment
 * 2) Ensures that any child of the sticky container (StickyScroll.Container) is a sticky element (StickyScroll.Segment)
 */
interface StickyScrollContextProps {
    defaultSegmentHeight?: number;
}

const StickyScrollContext = React.createContext<StickyScrollContextProps>({
    defaultSegmentHeight: undefined,
});

interface StickyScrollSegmentProps extends PropsWithChildren {
    clingToBottom?: boolean;
}

const Segment: React.FC<StickyScrollSegmentProps> = ({
    children,
    clingToBottom = false,
}) => (
    <div
        className={clingToBottom ? styles.clingToBottom : styles.stickySegment}
        data-testid="sticky-segment"
    >
        <div className={styles.stickyBox}>{children}</div>
    </div>
);

interface StickyScrollContainerProps extends PropsWithChildren {
    className: string;
    defaultSegmentHeight?: number;
    maxNumberOfSegments?: number;
}

const isStickySegment = (child: React.ReactNode) =>
    isValidElement(child) && child.type === Segment;
/** */
const Container: React.FC<StickyScrollContainerProps> = ({
    className,
    defaultSegmentHeight,
    maxNumberOfSegments = Infinity,
    children,
}) => {
    useEffect(() => {
        // Flattening children so that we can move any `StickyScroll.Segment` that is in a fragment to the top level
        React.Children.toArray(flattenChildren(children))
            .filter((child) => child !== null && child !== undefined)
            .forEach((child) => {
                if (!isStickySegment(child)) {
                    console.error(
                        `Invalid child detected in <StickyScroll.Container />: "${
                            React.isValidElement(child)
                                ? child.type.toString()
                                : child
                        }" detected in <StickyScroll.Container />`,
                        child
                    );
                }
            });
    }, [children]);

    const memoizedChildren = useMemo(
        () =>
            // Flattening children so that we can move any `StickyScroll.Segment` that is in a fragment to the top level
            React.Children.toArray(flattenChildren(children))
                .filter(isStickySegment)
                .slice(0, maxNumberOfSegments),
        [children, maxNumberOfSegments]
    );

    return (
        <StickyScrollContext.Provider value={{ defaultSegmentHeight }}>
            <div className={clsx([styles.stickyContainer, className])}>
                {memoizedChildren}
            </div>
        </StickyScrollContext.Provider>
    );
};

const StickyScroll = {
    Container,
    Segment,
};

export { StickyScroll };
